import { useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { connectUserEvents } from '../services/sse';
import { fetchUnlockedSongs } from '../services/api';
import { addUnlockedSongs } from '../services/audio';
import { useAppStore } from '../stores/appStore';
import type { NotificationEvent } from '../types';
import type { ToastNotification } from '../components/NotificationToast';

interface UseUserNotificationsOptions {
    onNotification?: (notification: ToastNotification) => void;
}

/**
 * Hook that connects to user SSE events and triggers notifications.
 * Automatically invalidates inbox queries when relevant notifications arrive.
 */
export function useUserNotifications({ onNotification }: UseUserNotificationsOptions = {}) {
    const queryClient = useQueryClient();
    const user = useAppStore(state => state.user);
    const notificationIdRef = useRef(0);

    // Generate unique notification IDs
    const generateId = useCallback(() => {
        notificationIdRef.current += 1;
        return `notification-${Date.now()}-${notificationIdRef.current}`;
    }, []);

    useEffect(() => {
        // Only connect if user is authenticated
        if (!user?.authenticated) {
            return;
        }

        const handleEvent = (event: NotificationEvent) => {
            console.log('[useUserNotifications] Received event:', event);
            const { payload } = event;

            if (!payload) {
                console.warn('[useUserNotifications] Event has no payload, ignoring');
                return;
            }

            // Invalidate inbox queries to refresh data
            console.log('[useUserNotifications] Invalidating queries...');
            queryClient.invalidateQueries({ queryKey: ['inbox'] });
            queryClient.invalidateQueries({ queryKey: ['collabInbox'] });

            // Create toast notification based on type
            let toast: ToastNotification | null = null;

            // The event type is at the top level, not inside payload
            switch (event.type) {
                case 'collab_request':  // Backend sends 'collab_request', not 'collab_invite'
                    toast = {
                        id: generateId(),
                        type: 'collab_invite',
                        title: 'Party Invite!',
                        message: payload.body || payload.message || 'Someone wants to play with you!',
                    };
                    // Also invalidate profile to update unread count/badge
                    queryClient.invalidateQueries({ queryKey: ['profile'] });
                    break;

                case 'collab_accepted':
                    toast = {
                        id: generateId(),
                        type: 'collab_invite',
                        title: 'Party Joined!',
                        message: payload.body || payload.message || 'Your party invite was accepted!',
                    };
                    // Invalidate game state so the party modal reflects the new player
                    queryClient.invalidateQueries({ queryKey: ['game'] });
                    break;

                case 'friend_request':
                    toast = {
                        id: generateId(),
                        type: 'friend_request',
                        title: 'Friend Request',
                        message: payload.body || payload.message || 'You have a new friend request',
                    };
                    // Also invalidate friend requests
                    queryClient.invalidateQueries({ queryKey: ['friendRequests'] });
                    break;

                case 'achievement':
                    if (payload.achievements && payload.achievements.length > 0) {
                        const achievement = payload.achievements[0];
                        toast = {
                            id: generateId(),
                            type: 'achievement',
                            title: 'Achievement Unlocked!',
                            message: achievement.name,
                        };

                        // Refresh unlocked songs since achievement might have unlocked a new song
                        fetchUnlockedSongs().then(response => {
                            if (response.songs && response.songs.length > 0) {
                                addUnlockedSongs(response.songs);
                            }
                        }).catch(() => {
                            // Silent fail - not critical
                        });
                    }
                    break;

                default:
                    // Generic notification
                    if (payload.message) {
                        toast = {
                            id: generateId(),
                            type: 'message',
                            title: 'New Message',
                            message: payload.body || payload.message,
                        };
                    }
            }

            if (toast && onNotification) {
                onNotification(toast);
            }
        };

        // Connect to user events SSE stream
        const disconnect = connectUserEvents(handleEvent);

        // Cleanup on unmount or when user changes
        return () => {
            disconnect();
        };
    }, [user?.authenticated, queryClient, onNotification, generateId]);
}

export default useUserNotifications;
